import { Emitter } from './emitter'
import MainModule from './main'
import { Storage } from './storage'
import { config } from '../constants/config'
import redaxios from 'redaxios'
import { cloneObject } from './utils'
import {
  AbstractSubscriber,
  AbstractSearchCore,
  MultipleResults,
  IFacet,
  ISearchConfig,
} from './search'
import { ConsoleError, ConsoleLog } from './logging'
import { getFromCache, storeInCache } from './cache'
import Fuse from 'fuse.js'
import { getDistance, isPointWithinRadius } from 'geolib'

export const GEOLOCATION_INDEX: string = 'cities'

// Constants for geolocation operations
const MAX_DISTANCE_FOR_LOCAL_CITY_METERS = 100000 // 100km radius
const DEFAULT_ANALYTICS_LOCATION = 'Hors Québec'

// Analytics error constants - descriptive values for GTM debugging
// Using lowercase snake_case for consistency with GTM conventions
const ANALYTICS_ERROR = {
  API_NOT_AVAILABLE: 'api_not_available',
  PERMISSION_DENIED: 'permission_denied',
  POSITION_UNAVAILABLE: 'position_unavailable',
  BROWSER_TIMEOUT: 'browser_timeout',
  TIMEOUT: 'timeout',
  DATA_UNAVAILABLE: 'data_unavailable',
  TRACKING_ERROR: 'tracking_error',
} as const

// Geolocation error codes from the Geolocation API
const GEOLOCATION_ERROR_CODE = {
  PERMISSION_DENIED: 1,
  POSITION_UNAVAILABLE: 2,
  TIMEOUT: 3,
} as const

/*

Whatever variable or functions with "Region" inside is about cities while the actual regions are named counties/county in the code.
When it's talking about "Locations", it's about cities but it basically fetches a bunch of objects that have cities and regions in them.
So it's a bit confusing but it's because of the way Algolia is set up.

TODO : Rename everything to be consistent with the new naming convention. (cities/regions)
*/

/**
 * Local distance calculator using geolib for geolocation-based operations
 * Replaces Algolia's geolocation features
 */
class LocalDistanceCalculator {
  /**
   * Find the nearest location to given coordinates
   */
  static getNearestLocation(lat: number, lng: number, cities: any[]): any {
    if (!cities.length) return null

    let nearest = null
    let minDistance = Infinity

    for (const city of cities) {
      if (!city.latitude || !city.longitude) continue

      const distance = getDistance(
        { latitude: lat, longitude: lng },
        { latitude: city.latitude, longitude: city.longitude }
      )

      if (distance < minDistance) {
        minDistance = distance
        nearest = city
      }
    }

    return nearest
  }

  /**
   * Get all locations within a specified radius
   */
  static getLocationsWithinRadius(lat: number, lng: number, radius: number, cities: any[]): any[] {
    if (!cities.length) return []

    return cities.filter(city => {
      if (!city.latitude || !city.longitude) return false

      return isPointWithinRadius(
        { latitude: city.latitude, longitude: city.longitude },
        { latitude: lat, longitude: lng },
        radius
      )
    })
  }

  /**
   * Calculate distance between two points in meters
   */
  static calculateDistance(lat1: number, lng1: number, lat2: number, lng2: number): number {
    return getDistance({ latitude: lat1, longitude: lng1 }, { latitude: lat2, longitude: lng2 })
  }

  /**
   * Sort locations by distance from a point
   */
  static sortByDistance(lat: number, lng: number, cities: any[]): any[] {
    return cities
      .map(city => ({
        ...city,
        distance:
          city.latitude && city.longitude
            ? getDistance(
                { latitude: lat, longitude: lng },
                { latitude: city.latitude, longitude: city.longitude }
              )
            : Infinity,
      }))
      .sort((a, b) => a.distance - b.distance)
  }
}

/**
 * Local search functionality using Fuse.js for fuzzy search
 * Replaces Algolia's search capabilities
 */
class LocalGeoSearch {
  private cityFuse: Fuse<any>
  private countyFuse: Fuse<any>
  private cities: any[] = []
  private counties: any[] = []

  constructor(cities: any[], counties: any[]) {
    this.cities = cities
    this.counties = counties

    // Configure Fuse.js for city search
    this.cityFuse = new Fuse(cities, {
      keys: [{ name: 'name', weight: 1.0 }],
      threshold: 0.4, // More lenient matching
      includeScore: true,
      minMatchCharLength: 1,
    })

    // Configure Fuse.js for county search
    this.countyFuse = new Fuse(counties, {
      keys: [{ name: 'county', weight: 1.0 }],
      threshold: 0.3, // Stricter matching for counties
      includeScore: true,
    })
  }

  /**
   * Search cities by name or partial name
   */
  searchCities(query: string, limit: number = 50): any[] {
    if (!query.trim()) return this.cities.slice(0, limit)
    const results = this.cityFuse.search(query, { limit })
    return results.map(result => result.item)
  }

  /**
   * Search counties by name or partial name
   */
  searchCounties(query: string, limit: number = 50): any[] {
    if (!query.trim()) return this.counties.slice(0, limit)

    // For single character searches, use prefix matching and sort alphabetically
    if (query.length === 1) {
      const prefixMatches = this.counties
        .filter(
          county => county.county && county.county.toLowerCase().startsWith(query.toLowerCase())
        )
        .sort((a, b) => a.county.localeCompare(b.county))
      return prefixMatches.slice(0, limit)
    }

    // For longer queries, use fuzzy search
    const results = this.countyFuse.search(query, { limit })
    return results.map(result => result.item)
  }

  /**
   * Get cities near a specific location within radius
   */
  getNearestCities(lat: number, lng: number, radius: number, limit: number = 50): any[] {
    const citiesInRadius = LocalDistanceCalculator.getLocationsWithinRadius(
      lat,
      lng,
      radius,
      this.cities
    )
    const sortedByDistance = LocalDistanceCalculator.sortByDistance(lat, lng, citiesInRadius)
    return sortedByDistance.slice(0, limit)
  }

  /**
   * Find a city by ID
   */
  getCityById(id: string): any {
    return this.cities.find(city => city.id === id)
  }

  /**
   * Find a county by ID
   */
  getCountyById(id: string): any {
    return this.counties.find(county => county.id === id)
  }
}

/**
 * Error handler for geo operations
 */
class GeoErrorHandler {
  static handleCacheFailure(): void {
    ConsoleError(true, 'Geo cache failure - using empty results for graceful degradation')
  }

  static handleSearchFailure(operation: string): void {
    ConsoleError(true, `Geo search failure in ${operation} - returning empty results`)
  }

  static showUserFriendlyError(message: string): void {
    // This could be enhanced to show user-facing error messages
    ConsoleError(true, `User-facing geo error: ${message}`)
  }
}

class GeoSubscriber extends AbstractSubscriber {
  core: AbstractSearchCore | null
  public initialized: boolean = false
  public readonly index: string = GEOLOCATION_INDEX

  private cached: any = {}
  private loading: any = {}

  constructor(groupId: string) {
    super(groupId)
    this.core = null
  }

  detaching(): void {
    throw new Error('Method not implemented.')
  }
  async attached(searchCore: AbstractSearchCore): Promise<void> {
    this.core = searchCore
    this.initialized = true
  }

  async onSearchConfigUpdate(config: ISearchConfig): Promise<void> {}

  async onSearchTextUpdate(text: string): Promise<void> {}

  async onFacetUpdate(facets: IFacet[]): Promise<void> {}

  async onResultUpdate(results: MultipleResults): Promise<void> {}

  async whenInitialized(): Promise<void> {
    return new Promise((resolve, reject) => {
      if (this.initialized) {
        resolve()
        return
      }
      let interval = setInterval(() => {
        if (this.initialized) {
          clearInterval(interval)

          resolve()
        }
      }, 10)
    })
  }

  async waitUntilNotLoading(key: string): Promise<void> {
    return new Promise((resolve, reject) => {
      if (!this.loading[key]) {
        resolve()
        return
      }
      let interval = setInterval(() => {
        if (!this.loading[key]) {
          clearInterval(interval)
          resolve()
        }
      }, 10)
    })
  }

  public async getNearestLocation(
    latitude: number,
    longitude: number,
    radius?: number,
    areaName?: string
  ): Promise<any> {
    let response = await this.getNearestLocations(latitude, longitude, radius, areaName)
    return response?.[0] || null
  }

  public async getNearestLocations(
    latitude: number,
    longitude: number,
    radius?: number,
    areaName?: string
  ): Promise<any[]> {
    let key = `NearestTo ${latitude} ${longitude} ${radius}`
    await this.waitUntilNotLoading(key)

    if (this.cached[key]) {
      return cloneObject(this.cached[key])
    }

    this.loading[key] = true
    const getResults = async (query?: string) =>
      await this.core?.executeSearch({
        index: ['cities'],
        options: {
          aroundRadius: { cities: radius ?? 1500000 },
          aroundLatLng: { cities: `${latitude},${longitude}` },
          page: { cities: 0 },
        },
        query: query ?? '',
      } as ISearchConfig)

    const areaSpecificResponse = await getResults(areaName)
    const closestResponse = await getResults()

    let response = areaSpecificResponse?.[this.index]?.items?.length
      ? areaSpecificResponse
      : closestResponse
    let result = (response as MultipleResults)?.[this.index]?.items

    this.cached[key] = result
    this.loading[key] = false
    return result
  }

  public async getNearestLocationByCityOrPostalCode(
    cityOrPostalCode: string,
    radius?: number
  ): Promise<any[]> {
    let response = await this.core?.executeSearch({
      query: cityOrPostalCode,
      index: ['cities'],
      options: {
        aroundRadius: { cities: radius ?? 15000 },
        aroundLatLng: { cities: null },
        page: { cities: 0 },
      },
    } as ISearchConfig)

    let result = (response as MultipleResults)?.[this.index]?.items
    return result
  }

  public async getNearestCountiesByCityOrPostalCode(
    cityOrPostalCode: string,
    radius?: number
  ): Promise<any[]> {
    let response = await this.core?.executeSearch({
      query: cityOrPostalCode,
      index: ['cities'],
      options: {
        aroundRadius: { cities: radius ?? 15000 },
        aroundLatLng: { cities: null },
        page: { cities: 0 },
      },
    } as ISearchConfig)

    let result = (response as MultipleResults)?.[this.index]?.items
    return result
  }

  public async getById(id: string): Promise<any> {
    let response = await this.core?.executeSearch({
      objectId: id,
      index: ['cities'],
    } as ISearchConfig)

    return (response as MultipleResults)?.[this.index]?.items?.[0]
  }

  public async getAllLocations(): Promise<any[]> {
    try {
      const citiesFromCache = getFromCache('algolia_cities')
      if (citiesFromCache) {
        return citiesFromCache
      } else {
        let response = await redaxios.get(`${config.backendApi}/content/getAlgoliaCities`)
        if (!response?.data) {
          throw new Error('No data returned from /getAlgoliaCities endpoint')
        }
        storeInCache('algolia_cities', response.data, 1000 * 60 * 60) // cache for 1hr
        return response?.data || []
      }
    } catch (e) {
      ConsoleError(
        true,
        'Could not fetch /getAlgoliaCities endpoint, falling back to Algolia search'
      )
      // If the endpoint is not available, we will fallback to the Algolia search
      return []
    }
  }

  public async getAllCounties(): Promise<any[]> {
    // Simple function to get all unique counties from the list of locations
    try {
      const cities = await this.getAllLocations() // will either get from local storage or fetch from the endpoint
      if (!cities.length) {
        throw new Error('No cities fetched from /getAlgoliaCities endpoint')
      }
      // filter out duplicate counties
      let counties: any[] = []
      cities.forEach(city => {
        if (
          city.county &&
          !counties.find(county => {
            return county.county === city.county
          })
        ) {
          counties.push(city)
        }
      })
      // return counties sorted alphabetically by county name
      return counties.sort((a, b) => a.county.localeCompare(b.county))
    } catch (e) {
      ConsoleError(
        true,
        'Could not fetch counties from /getAlgoliaCities endpoint, falling back to Algolia search'
      )
      // If the endpoint is not available, we will fallback to the Algolia search
      return []
    }
  }
}

export class Geo {
  public static async getGeo(): Promise<any> {
    try {
      const { coords } = await new Promise<any>((resolve, reject) => {
        navigator.geolocation.getCurrentPosition(resolve, reject, {
          enableHighAccuracy: true,
        })
      })
      return {
        longitude: coords.longitude,
        latitude: coords.latitude,
      }
    } catch (error) {
      ConsoleError(true, 'Failed to get location through navigator.geolocation', error)
      return null
    }
  }

  private _allLocations: any[] = []
  private _allCounties: any[] = []
  private _localGeoSearch: LocalGeoSearch | null = null
  private geoSubscriber: GeoSubscriber = new GeoSubscriber('geolocation')
  private _currentLocation: any = null
  private _storage: Storage = new Storage({ useSession: true })
  private _cached: any = {}

  constructor(
    private ee: Emitter,
    private main: MainModule
  ) {
    try {
      main.getSearchManager().subscribe(this.geoSubscriber)
      this.fetchPreviousLocation()
      // init() is async and runs in background - errors are handled internally
      this.init().catch(error => {
        ConsoleError(true, 'Geo initialization failed (non-blocking)', error)
      })
    } catch (error) {
      ConsoleError(true, 'Geo constructor failed (non-blocking)', error)
    }
  }

  private fetchPreviousLocation() {
    const locationString = this._storage.getItem('geo_location')
    if (locationString) {
      try {
        const location = JSON.parse(locationString)
        this.updateLocation(location)
      } catch (error) {
        ConsoleError(true, 'Failed to parse stored location data', error)
        // Clear invalid data from storage
        this._storage.removeItem('geo_location')
      }
    }
  }

  public get currentLocation(): any {
    return this._currentLocation
  }
  public set currentLocation(value: any) {
    this.updateLocation(value)
  }

  public get allLocations(): any[] {
    return this._allLocations
  }

  public async getAllCounties(): Promise<any[]> {
    return await this.geoSubscriber.getAllCounties()
  }

  private async populateAllLocations() {
    this._allLocations = await this.listAll()
    this._allCounties = await this.getAllCounties()

    // Initialize local search once we have the data
    if (this._allLocations.length > 0 && this._allCounties.length > 0) {
      this._localGeoSearch = new LocalGeoSearch(this._allLocations, this._allCounties)
    }
  }

  /**
   * Get the local search instance, initializing if needed
   * Includes retry logic for resilience against transient failures
   */
  private async getLocalGeoSearch(): Promise<LocalGeoSearch | null> {
    if (!this._localGeoSearch) {
      try {
        await this.populateAllLocations()

        // Retry once if first attempt failed to populate locations
        if (this._allLocations.length === 0) {
          ConsoleLog(true, 'First attempt to populate locations returned empty, retrying...')
          await this.populateAllLocations()
        }
      } catch (e) {
        // Retry once on error
        try {
          ConsoleLog(true, 'First attempt to populate locations failed, retrying...')
          await this.populateAllLocations()
        } catch (retryError) {
          GeoErrorHandler.handleCacheFailure()
          return null
        }
      }
    }
    return this._localGeoSearch
  }

  /**
   * Determine analytics location data based on browser coordinates
   * Returns city/region data or an error reason for debugging
   */
  private determineAnalyticsLocation(
    latitude: number,
    longitude: number
  ): { city: string; region: string; errorReason?: string } {
    if (!this._allLocations.length) {
      ConsoleError(true, 'No locations available for analytics determination')
      return {
        city: ANALYTICS_ERROR.DATA_UNAVAILABLE,
        region: ANALYTICS_ERROR.DATA_UNAVAILABLE,
        errorReason: 'no_locations_loaded',
      }
    }

    const nearestCity = LocalDistanceCalculator.getNearestLocation(
      latitude,
      longitude,
      this._allLocations
    )

    if (!nearestCity) {
      return {
        city: ANALYTICS_ERROR.DATA_UNAVAILABLE,
        region: ANALYTICS_ERROR.DATA_UNAVAILABLE,
        errorReason: 'no_nearest_city_found',
      }
    }

    // Validate that nearestCity has required properties
    if (!nearestCity.latitude || !nearestCity.longitude || !nearestCity.name) {
      ConsoleError(true, 'Nearest city missing required properties', nearestCity)
      return {
        city: ANALYTICS_ERROR.DATA_UNAVAILABLE,
        region: ANALYTICS_ERROR.DATA_UNAVAILABLE,
        errorReason: 'city_missing_properties',
      }
    }

    const distanceToCity = LocalDistanceCalculator.calculateDistance(
      latitude,
      longitude,
      nearestCity.latitude,
      nearestCity.longitude
    )
    if (distanceToCity <= MAX_DISTANCE_FOR_LOCAL_CITY_METERS) {
      return {
        city: nearestCity.name,
        region: nearestCity.region || DEFAULT_ANALYTICS_LOCATION,
      }
    }

    return {
      city: DEFAULT_ANALYTICS_LOCATION,
      region: DEFAULT_ANALYTICS_LOCATION,
    }
  }

  /**
   * Map geolocation error code to descriptive analytics value
   */
  private getGeolocationErrorValue(errorCode: number): string {
    switch (errorCode) {
      case GEOLOCATION_ERROR_CODE.PERMISSION_DENIED:
        return ANALYTICS_ERROR.PERMISSION_DENIED
      case GEOLOCATION_ERROR_CODE.POSITION_UNAVAILABLE:
        return ANALYTICS_ERROR.POSITION_UNAVAILABLE
      case GEOLOCATION_ERROR_CODE.TIMEOUT:
        return ANALYTICS_ERROR.BROWSER_TIMEOUT
      default:
        return ANALYTICS_ERROR.TRACKING_ERROR
    }
  }

  /**
   * Track browser location for analytics (GA4)
   * This should be called on every page load to send fresh geolocation data to GTM
   */
  public async trackBrowserLocationForAnalytics(): Promise<void> {
    try {
      // First, ensure locations are loaded before proceeding
      // This fixes the race condition where geolocation callback fires before data is ready
      await this.getLocalGeoSearch()

      // Verify locations are actually populated after retry logic
      if (this._allLocations.length === 0) {
        console.warn('Location data unavailable after retries')
        await this.main.analyticsTrack('geolocation_update', {
          localisation_gps_usager: ANALYTICS_ERROR.DATA_UNAVAILABLE,
          region_gps_usager: ANALYTICS_ERROR.DATA_UNAVAILABLE,
        })
        return
      }

      if (!('geolocation' in navigator)) {
        console.warn('Geolocation API not available')
        await this.main.analyticsTrack('geolocation_update', {
          localisation_gps_usager: ANALYTICS_ERROR.API_NOT_AVAILABLE,
          region_gps_usager: ANALYTICS_ERROR.API_NOT_AVAILABLE,
        })
        return
      }

      let responded = false
      const safetyTimer = setTimeout(() => {
        if (!responded) {
          responded = true // Prevent duplicate pushes
          console.warn('Geolocation timeout fallback (15s) → pushing timeout analytics data')
          this.main.analyticsTrack('geolocation_update', {
            localisation_gps_usager: ANALYTICS_ERROR.TIMEOUT,
            region_gps_usager: ANALYTICS_ERROR.TIMEOUT,
          })
        }
      }, 15000) // Increased from 8s to 15s to allow more time for user permission decisions

      navigator.geolocation.getCurrentPosition(
        position => {
          if (responded) return // Safety timer already fired, don't push again
          responded = true
          clearTimeout(safetyTimer)
          const { latitude, longitude } = position.coords

          const analyticsData = this.determineAnalyticsLocation(latitude, longitude)

          // Always push data - determineAnalyticsLocation now returns descriptive errors
          this.main.analyticsTrack('geolocation_update', {
            localisation_gps_usager: analyticsData.city,
            region_gps_usager: analyticsData.region,
          })

          if (analyticsData.errorReason) {
            console.warn(
              `Could not determine precise analytics location: ${analyticsData.errorReason}`
            )
          }
        },
        error => {
          if (responded) return // Safety timer already fired, don't push again
          responded = true
          clearTimeout(safetyTimer)

          const errorValue = this.getGeolocationErrorValue(error.code)
          console.warn(`Geolocation error (code ${error.code}): ${error.message} → ${errorValue}`)

          this.main.analyticsTrack('geolocation_update', {
            localisation_gps_usager: errorValue,
            region_gps_usager: errorValue,
          })
        },
        { enableHighAccuracy: true, timeout: 10000, maximumAge: 0 } // Browser timeout: 10s (less than our 15s safety)
      )
    } catch (error) {
      ConsoleError(true, 'Error in trackBrowserLocationForAnalytics', error)
      // Push tracking_error so GTM knows an exception occurred
      await this.main.analyticsTrack('geolocation_update', {
        localisation_gps_usager: ANALYTICS_ERROR.TRACKING_ERROR,
        region_gps_usager: ANALYTICS_ERROR.TRACKING_ERROR,
      })
    }
  }

  public async init(): Promise<void> {
    try {
      if (this.currentLocation) {
        return
      }

      let location = await Geo.getGeo()
      if (this.geoSubscriber) {
        await this.geoSubscriber.whenInitialized()
      }

      await this.populateAllLocations()

      if (location) {
        let locationObject = await this.geoSubscriber.getNearestLocation(
          location.latitude,
          location.longitude
        )
        await this.updateLocation(locationObject)
      }
    } catch (error) {
      ConsoleError(true, 'Geo init failed (non-blocking)', error)
    }
  }

  public async getAutomaticLocation(): Promise<any> {
    try {
      if (this.geoSubscriber) {
        await this.geoSubscriber.whenInitialized()
      }
      let location = await Geo.getGeo()
      if (location) {
        let locationObject = await this.geoSubscriber.getNearestLocation(
          location.latitude,
          location.longitude
        )
        return locationObject
      } else {
        ConsoleLog(true, 'no location')
        return null
      }
    } catch (error) {
      ConsoleError(true, 'getAutomaticLocation failed', error)
      return null
    }
  }

  public async setAutomaticLocation(): Promise<void> {
    try {
      if (this.geoSubscriber) {
        await this.geoSubscriber.whenInitialized()
      }

      let location = await Geo.getGeo()
      if (location) {
        let locationObject = await this.geoSubscriber.getNearestLocation(
          location.latitude,
          location.longitude
        )
        await this.updateLocation(locationObject)
      } else {
        ConsoleLog(true, 'no location')
      }
    } catch (error) {
      ConsoleError(true, 'setAutomaticLocation failed', error)
    }
  }

  private async updateLocation(location: any) {
    this._currentLocation = location
    this.ee.emit('geo:location', location)
    this._storage.setItem('geo_location', JSON.stringify(location))
  }

  public async getById(id: string): Promise<any> {
    await this.geoSubscriber.whenInitialized()
    let result = await this.geoSubscriber.getById(id)
    return result
  }

  public async listAll(): Promise<any[]> {
    await this.geoSubscriber.whenInitialized()
    let results = await this.geoSubscriber.getAllLocations()
    return results
  }

  public async getNearestLocation(
    lat: number,
    lng: number,
    radius: number = 1500,
    areaName?: string
  ): Promise<any> {
    try {
      const localSearch = await this.getLocalGeoSearch()
      if (localSearch) {
        // Use local search for better performance
        return LocalDistanceCalculator.getNearestLocation(lat, lng, this._allLocations)
      } else {
        // Fallback to Algolia if local search fails
        await this.geoSubscriber.whenInitialized()
        return await this.geoSubscriber.getNearestLocation(lat, lng, radius, areaName)
      }
    } catch (e) {
      GeoErrorHandler.handleSearchFailure('getNearestLocation')
      return null
    }
  }

  public async listNearMe(radius?: number): Promise<any[]> {
    try {
      if (!this.currentLocation) {
        return []
      }

      const localSearch = await this.getLocalGeoSearch()
      if (localSearch) {
        // Use local search for better performance
        const radiusInMeters = radius || 15000 // Default 15km radius
        return localSearch.getNearestCities(
          this.currentLocation.latitude,
          this.currentLocation.longitude,
          radiusInMeters
        )
      } else {
        // Fallback to Algolia if local search fails
        await this.geoSubscriber.whenInitialized()
        return await this.geoSubscriber.getNearestLocations(
          this.currentLocation.latitude,
          this.currentLocation.longitude,
          radius
        )
      }
    } catch (e) {
      GeoErrorHandler.handleSearchFailure('listNearMe')
      return []
    }
  }

  public async list(cityOrPostalCode: string, index?: string): Promise<any[]> {
    try {
      const localSearch = await this.getLocalGeoSearch()
      if (localSearch) {
        // Use local search for better performance
        return localSearch.searchCities(cityOrPostalCode)
      } else {
        // Fallback to Algolia if local search fails
        await this.geoSubscriber.whenInitialized()
        return await this.geoSubscriber.getNearestLocationByCityOrPostalCode(cityOrPostalCode)
      }
    } catch (e) {
      GeoErrorHandler.handleSearchFailure('list')
      return []
    }
  }

  // function that targets specific index for county search, sorted by A-Z
  public async listCounties(value: string): Promise<any[]> {
    try {
      const localSearch = await this.getLocalGeoSearch()
      if (localSearch) {
        // Use local search for better performance - no more Algolia calls!
        return localSearch.searchCounties(value)
      } else {
        // Fallback to Algolia if local search fails
        const searchIndex = 'cities_county_asc'
        await this.geoSubscriber.whenInitialized()
        const searchConfig = {
          query: value,
          index: [searchIndex],
          options: {
            aroundLatLng: { [searchIndex]: null },
            page: { [searchIndex]: 0 },
          },
        }
        let results = await this.geoSubscriber.core?.executeSearch(searchConfig)
        if (results && results?.[searchIndex]?.items) {
          // filter out duplicates counties
          let uniqueCounties: any[] = []
          const countyResults = results[searchIndex].items
          countyResults.forEach(county => {
            if (
              !uniqueCounties.find(c => {
                return c.county === county.county
              })
            ) {
              uniqueCounties.push(county)
            }
          })
          return uniqueCounties
        } else {
          return []
        }
      }
    } catch (e) {
      GeoErrorHandler.handleSearchFailure('listCounties')
      return []
    }
  }

  public async getLatLngByPostalCode(postalCode: string): Promise<any> {
    try {
      let result = await redaxios.get(
        `${config.geocodingApi}?components=postal_code:${postalCode}|country:CA&key=${import.meta.env.VITE_FIREBASE_API_KEY || ''}`
      )
      let location = result?.data?.results?.[0]?.geometry?.location
      return { ...location, response: result }
    } catch (e) {
      return null
    }
  }

  public async getNearestLocationByPostalCode(postalCode: string): Promise<any> {
    let location = await this.getLatLngByPostalCode(postalCode)
    const postcodeLocalities = location.response?.data?.results?.[0]?.postcode_localities
    const addressComponents = location.response?.data?.results?.[0]?.address_components
    let areaName
    if (postcodeLocalities?.length) {
      areaName = postcodeLocalities[0] // take the first one, it should be the closest one
    } else if (addressComponents) {
      areaName = addressComponents.filter(
        (component: { long_name: string; short_name: string; types: string[] }) =>
          component.types.find((type: string) => type !== 'postal_code')
      )[0].long_name
    }

    if (!location) {
      return null
    }

    let nearestLocation = await this.getNearestLocation(
      location.lat,
      location.lng,
      150000,
      areaName
    )
    return nearestLocation
  }

  public async whenReady(): Promise<void> {
    await this.geoSubscriber.whenInitialized()
  }
}
